// src/features/groups/components/GroupList.jsx

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UsersRound, Plus, Search, AlertCircle } from 'lucide-react';
import useGroups from '../hooks/useGroups';
import { Avatar, AVATAR_VARIANTS } from '../../../components/avatars';
import './GroupList.css';

const GroupList = ({ onCreateGroup }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groups, status, formatTime, loadError, isOffline } = useGroups();
  const [searchQuery, setSearchQuery] = useState('');

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const hasQuery = normalizedQuery.length > 0;

  const filteredGroups = groups.filter((group) => {
    const name = group.name?.trim().toLowerCase() || '';
    if (!hasQuery) return true;
    return name.includes(normalizedQuery);
  });

  const fallbackLoadErrorMessage = t('groups.load_error', 'Failed to load groups. Please try again.');
  const loadErrorMessage = loadError ? t(loadError, fallbackLoadErrorMessage) : fallbackLoadErrorMessage;
  const offlineMessage = t('groups.offline_error', "You're offline. Connect to the internet to load your groups.");

  // State mapping for UI
  const showSkeleton = status === 'loading';
  const showErrorState = status === 'error';
  const showOfflineState = status === 'offline';
  const showEmptyState = status === 'empty' && groups.length === 0 && !hasQuery && !loadError;
  const showNoResults =
    (status === 'ready' || status === 'empty') &&
    hasQuery &&
    filteredGroups.length === 0;

  const toDisplayDate = (value) => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    return undefined;
  };

  const SkeletonGroupItem = () => (
    <div className="group-item group-list-skeleton">
      <div className="group-avatar group-list-skeleton-avatar" />
      <div className="group-details">
        <div className="group-details-top">
          <div className="group-list-skeleton-text group-list-skeleton-name" />
          <div className="group-list-skeleton-text group-list-skeleton-time" />
        </div>
        <div className="group-details-bottom">
          <div className="group-list-skeleton-text group-list-skeleton-message" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="group-list">
      <div className="group-list-header">
        <div className="header-content">
          <div className="header-title">
            <UsersRound className="title-icon" />
            <h1>{t('chat.groups')}</h1>
          </div>
          <button
            onClick={onCreateGroup}
            className="create-group-button"
            aria-label="Create new group"
          >
            <Plus />
          </button>
        </div>

        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder={t('groups.search_groups')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="group-list-content">
        {/* Inline banners when we have something to show */}
        {!showSkeleton && status === 'ready' && loadError && (
          <div className="group-list-error-banner" role="alert">
            <AlertCircle size={16} />
            <span>{loadErrorMessage}</span>
          </div>
        )}

        {!showSkeleton && status === 'ready' && !loadError && isOffline && (
          <div className="group-list-error-banner" role="status">
            <AlertCircle size={16} />
            <span>{offlineMessage}</span>
          </div>
        )}

        {showSkeleton ? (
          // Show skeleton items while loading or before hydration
          Array(10).fill(0).map((_, index) => (
            <SkeletonGroupItem key={index} />
          ))
        ) : showErrorState ? (
          <div className="group-list-empty" role="alert">
            <AlertCircle className="empty-icon" />
            <p>{loadErrorMessage}</p>
          </div>
        ) : showOfflineState ? (
          <div className="group-list-empty" role="alert">
            <AlertCircle className="empty-icon" />
            <p>{offlineMessage}</p>
          </div>
        ) : showEmptyState ? (
          // Show empty state only after hydration confirms no groups
          <div className="group-list-empty">
            <UsersRound className="empty-icon" />
            <p>{t('groups.no_groups', 'No groups yet')}</p>
            <button onClick={onCreateGroup} className="create-first-group-button">
              {t('groups.create_first', 'Create your first group')}
            </button>
          </div>
        ) : showNoResults ? (
          // Show no results message when search has no matches
          <div className="group-list-empty">
            <Search className="empty-icon" />
            <p>{t('groups.no_results', 'No groups found')}</p>
          </div>
        ) : (
          // Show groups list
          filteredGroups.map((group) => {
            const displayName = group.name?.trim() || t('groups.untitled_group', 'Untitled group');
            const lastMessagePreview =
              group.lastMessage?.kind === 'system'
                ? t('chat.no_messages_yet', 'No messages yet')
                : group.lastMessage?.text || t('chat.no_messages_yet', 'No messages yet');
            const lastActiveAt = toDisplayDate(group.lastActivityAt ?? group.lastMessageAt);

            return (
              <button
                key={group.id}
                onClick={() => navigate(`/groups/${group.id}`)}
                className={`group-item ${group.unreadCount > 0 ? 'unread' : ''}`}
              >
                <div className="group-avatar">
                  <Avatar
                    variant={AVATAR_VARIANTS.GROUP}
                    name={displayName}
                    src={group.icon}
                    className="group-avatar-avatar"
                    alt={displayName}
                    fallbackColor={group.unreadCount > 0 ? 'var(--accent-primary)' : undefined}
                    textColor={group.unreadCount > 0 ? 'var(--text-inverse)' : 'var(--background-elevated)'}
                  />
                </div>

                <div className="group-details">
                  <div className="group-details-top">
                    <h3 className="group-name">{displayName}</h3>
                    <span className="last-active">
                      {formatTime(lastActiveAt)}
                    </span>
                  </div>
                  <div className="group-details-bottom">
                    <p className="last-message">
                      {lastMessagePreview}
                    </p>
                    {group.unreadCount > 0 && (
                      <span className="unread-badge">
                        {group.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default GroupList;
